import { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { AnchorAPI } from 'modules/AnchorAPI';
import { Part } from 'modules/AnchorAPI/v3/upload/uploadFileMultipart';
import { Data } from 'modules/AnchorAPI/v3/upload/fetchAudioProcessingStatus';

function useProcessFile({
  onProcessingSuccess,
  onError,
}: {
  onProcessingSuccess: (data: Data | null) => void;
  onError: () => void;
}) {
  const [requestId, setRequestId] = useState<string | null>(null);
  const [stopPolling, setStopPolling] = useState(false);

  /* 
    Create mutation function that will  
    start the processing. When triggered successfully,
    the requestUuid is stored in state as the requestId
    which will enable the useQuery function below to run
  */
  const { mutate } = useMutation(
    ({
      file,
      completedParts,
      requestUuid,
      uploadId,
      webEpisodeId,
    }: {
      file: File;
      completedParts: Part[];
      requestUuid: string;
      uploadId: string;
      webEpisodeId: string;
    }) => {
      const isVideoFile = !!file.type.match('video');
      return AnchorAPI.startMediaProcessing({
        uploadId,
        requestUuid,
        parts: completedParts,
        caption: file.name || new Date().toLocaleDateString(),
        uploadType: isVideoFile ? 'video' : 'default',
        isMultipartUpload: true,
        isExtractedFromVideo: isVideoFile !== null,
        origin: 'episode-media:upload',
        webEpisodeId,
      });
    },
    {
      onMutate: () => {
        setStopPolling(false);
      },
      onError: () => {
        onError();
      },
      onSuccess: res => {
        setRequestId(res.requestUuid);
      },
    }
  );

  /* 
    Poll the processing status endpoint 
    every 3 seconds until state === 'processed'
    or there is an error. Polling will only start
    when a requestId is present. 
  */
  const { data } = useQuery(
    ['processProgress', requestId],
    () => {
      return AnchorAPI.fetchMediaProcessingStatus({ requestUuid: requestId! });
    },
    {
      onSuccess: res => {
        if (res?.request.state === 'processed') {
          setStopPolling(true);
          setRequestId(null);
          onProcessingSuccess(res.data);
        }
      },
      onError: () => {
        setStopPolling(true);
        setRequestId(null);
        onError();
      },
      enabled: requestId !== null,
      refetchInterval: stopPolling ? false : 3000,
      refetchIntervalInBackground: true,
    }
  );

  return { processFile: mutate, data };
}

export { useProcessFile };
